import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNewOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import StarIcon from "@mui/icons-material/StarRounded";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMoreOutlined";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLessOutlined";
import FlashOnOutlinedIcon from "@mui/icons-material/FlashOnOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { getStoreOptions } from "../../api/adminService";
import { getCategories, getCategoryAttributes } from "../../api/categoryService";
import { getStoreCategories } from "../../api/storeService";
import { createProduct, createProductVariant, addProductImageFromUrl, addVariantImageFromUrl } from "../../api/productService";

// The two browser extensions we scrape products with. Each has its own JSON
// export shape (see the normalize* functions below) but both render through
// the same product/variant UI here.
const SOURCES = {
  quickscrape: {
    label: "QuickScrape",
    icon: FlashOnOutlinedIcon,
    hint: 'Scrape a page with QuickScrape ("Products" extractor), click "Download JSON", then upload that file here.',
  },
  productscout: {
    label: "Product Scout",
    icon: TravelExploreOutlinedIcon,
    hint: 'Extract a product with Product Scout, click "JSON" (or "Export all saved"), then upload that file here.',
  },
};

// Matches the backend's ProductAttribute.InputType enum (Models/Enums.cs).
const ATTRIBUTE_INPUT_DROPDOWN = 1;
const ATTRIBUTE_INPUT_NUMBER = 3;

// QuickScrape's "Download JSON" exports one object per product, each with a
// nested `variants` array (see the extension's popup.js downloadJson handler).
// v3.0 added product-level `category`/`description` and variant-level
// `barcode`/`weight`/`image`; v2.1 only had the fields without those. Older
// exports (v2.0) were flat: one row per variant, with capitalized column
// names. v3.8 keeps the same grouped shape. We normalize all shapes into the
// grouped shape below, so missing fields just render as blank.
function isGroupedShape(items) {
  return items.length > 0 && items.every((item) => Array.isArray(item?.variants));
}

function isFlatProductShape(items) {
  return items.length > 0 && items.some((item) => "Product" in item || "Variant" in item);
}

function normalizeGrouped(items) {
  return items.map((p) => ({
    product: p.product || "(no name)",
    brand: p.brand || "",
    category: p.category || "",
    description: p.description || "",
    currency: p.currency || "",
    rating: p.rating || "",
    reviews: p.reviews || "",
    link: p.link || "",
    images: Array.isArray(p.images) ? p.images.filter(Boolean) : [],
    source: p.source || "",
    variants: (p.variants || []).map((v) => ({
      variant: v.variant || "",
      options: v.options || "",
      sku: v.sku || "",
      barcode: v.barcode || "",
      price: v.price || "",
      was: v.was || "",
      discount: v.discount || "",
      qty: v.qty || "",
      weight: v.weight || "",
      availability: v.availability || "",
      image: v.image || "",
    })),
  }));
}

function normalizeFlat(items) {
  const byKey = new Map();
  const products = [];
  items.forEach((r) => {
    const key = `${r.Product || ""}|${r.Link || ""}`.toLowerCase();
    let p = byKey.get(key);
    if (!p) {
      p = {
        product: r.Product || "(no name)",
        brand: r.Brand || "",
        category: r.Category || "",
        description: r.Description || "",
        currency: r.Currency || "",
        rating: r.Rating || "",
        reviews: r.Reviews || "",
        link: r.Link || "",
        images: r.Images ? String(r.Images).split("|").map((s) => s.trim()).filter(Boolean) : [],
        source: r.Source || "",
        variants: [],
      };
      byKey.set(key, p);
      products.push(p);
    }
    p.variants.push({
      variant: r.Variant || "",
      options: r.Options || "",
      sku: r.SKU || "",
      barcode: r.Barcode || "",
      price: r.Price || "",
      was: r.Was || "",
      discount: r.Discount || "",
      qty: r.Qty || "",
      weight: r.Weight || "",
      availability: r.Availability || "",
      image: r["Variant Image"] || "",
    });
  });
  return products;
}

// Product Scout's "JSON" export downloads a single merged product object
// (name, brand, pricing.{current,original}, identifiers.{sku,gtin,...},
// variants[].{title,options,price,compareAtPrice,available,...}). Its
// "Export all saved" downloads an array of `{ savedAt, product }` wrappers
// around that same shape. We unwrap the wrapper (if present) and map it onto
// the same grouped product/variant shape the rest of this page renders.
function unwrapProductScoutItem(item) {
  if (item && typeof item === "object" && !item.meta && item.product && typeof item.product === "object") {
    return item.product;
  }
  return item;
}

function isProductScoutShape(items) {
  return (
    items.length > 0 &&
    items.every((item) => {
      const p = unwrapProductScoutItem(item);
      return p && typeof p === "object" && (p.meta || p.identifiers || p.pricing);
    })
  );
}

function formatAvailabilityEnum(a) {
  const map = {
    in_stock: "In stock",
    out_of_stock: "Out of stock",
    preorder: "Pre-order",
    backorder: "Backorder",
    limited: "Limited",
    discontinued: "Discontinued",
  };
  if (!a) return "";
  return map[a] || a;
}

function computeDiscount(price, was) {
  if (typeof price !== "number" || typeof was !== "number" || !(was > price)) return "";
  return `${Math.round((1 - price / was) * 100)}%`;
}

function normalizeProductScout(items) {
  return items.map((raw) => {
    const p = unwrapProductScoutItem(raw) || {};
    const pricing = p.pricing || {};
    const identifiers = p.identifiers || {};
    const baseAvailability = formatAvailabilityEnum(p.availability);
    const variantsSrc = Array.isArray(p.variants) && p.variants.length > 0 ? p.variants : [null];

    return {
      product: p.name || "(no name)",
      brand: p.brand || "",
      category: p.category || "",
      description: p.description || "",
      currency: pricing.currency || "",
      rating: p.rating?.value ?? "",
      reviews: p.rating?.count ?? "",
      link: p.meta?.url || p.meta?.canonicalUrl || "",
      images: Array.isArray(p.images) && p.images.length > 0 ? p.images.filter(Boolean) : p.mainImage ? [p.mainImage] : [],
      source: p.meta?.siteName || p.meta?.domain || "",
      variants: variantsSrc.map((v) => {
        const price = v?.price ?? pricing.current ?? "";
        const was = v?.compareAtPrice ?? pricing.original ?? "";
        const options =
          v?.options && typeof v.options === "object"
            ? Object.entries(v.options)
                .map(([k, val]) => `${k}: ${val}`)
                .join(" | ")
            : "";
        const availability =
          v && typeof v.available === "boolean" ? (v.available ? "In stock" : "Out of stock") : baseAvailability;

        return {
          variant: v?.title || "",
          options,
          sku: v?.sku || identifiers.sku || "",
          barcode: identifiers.gtin || identifiers.mpn || identifiers.asin || "",
          price,
          was,
          discount: computeDiscount(price, was),
          qty: "",
          weight: "",
          availability,
          image: v?.image || "",
        };
      }),
    };
  });
}

function availabilityColor(text) {
  const t = (text || "").toLowerCase();
  if (t.includes("out of stock") || t.includes("sold out")) return "error";
  if (t.includes("in stock")) return "success";
  if (t.includes("pre-order")) return "info";
  return "default";
}

function formatPrice(price, currency) {
  if (price === "" || price == null) return "—";
  return currency ? `${price} ${currency}` : String(price);
}

function toNumber(value) {
  if (value === "" || value == null) return null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  if (!cleaned) return null;
  const n = parseFloat(cleaned);
  return Number.isNaN(n) ? null : n;
}

// Leaf categories only, that the target store has enabled - mirrors the
// backend's ValidateCategoryAsync check (Services/ProductValidation.cs).
// Same helper as Admin/ProductImports.js and BusinessOwner/Products.js.
function buildCategoryOptions(categories, storeCategories) {
  const parentIds = new Set();
  const byId = new Map();
  categories.forEach((c) => {
    byId.set(c.categoryId, c);
    if (c.parentCategoryId != null) parentIds.add(c.parentCategoryId);
  });

  const pathFor = (categoryId) => {
    const parts = [];
    let current = byId.get(categoryId);
    while (current) {
      parts.unshift(current.name);
      current = current.parentCategoryId != null ? byId.get(current.parentCategoryId) : null;
    }
    return parts.join(" › ");
  };

  return storeCategories
    .filter((sc) => !parentIds.has(sc.categoryId))
    .map((sc) => ({ categoryId: sc.categoryId, name: pathFor(sc.categoryId) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function hasRealVariants(product) {
  if (product.variants.length > 1) return true;
  if (product.variants.length === 1) {
    const v = product.variants[0];
    return Boolean(v.variant || v.options);
  }
  return false;
}

// Starting point for a product's "prepare before adding to store" draft -
// pre-fills from the scraped data so the admin usually only has to pick a
// category, not retype everything.
function seedDraft(product) {
  const firstVariant = product.variants?.[0] || {};
  return {
    name: product.product || "",
    description: product.description || "",
    price: toNumber(firstVariant.price) ?? "",
    stockQuantity: toNumber(firstVariant.qty) ?? 0,
    sku: firstVariant.sku || "",
    categoryId: "",
    imageSelection: product.images.map((_, i) => i),
    attributeValues: {},
    variantOverrides: {},
    ready: false,
  };
}

// Per-variant editable rows (attribute mapping, price, stock, sku), starting
// from whatever the draft already saved, else the scraped values.
function initialVariantRows(product, draft) {
  return product.variants.map((v, vi) => {
    const saved = draft?.variantOverrides?.[vi];
    return {
      attributeSelections: saved?.attributeSelections || {},
      price: saved?.price ?? (toNumber(v.price) ?? ""),
      stockQuantity: saved?.stockQuantity ?? (toNumber(v.qty) ?? 0),
      sku: saved?.sku ?? (v.sku || ""),
    };
  });
}

// Best-effort auto-match of each detected variant's raw text (e.g. "Black / M")
// against the category's variant option values. Rows already mapped are kept.
function guessVariantSelections(product, variantAttributes, rows) {
  return rows.map((row, vi) => {
    if (Object.keys(row.attributeSelections || {}).length > 0) return row;
    const v = product.variants[vi] || {};
    const text = `${v.variant || ""} ${v.options || ""}`.toLowerCase();
    const guesses = {};
    variantAttributes.forEach((attr) => {
      const match = (attr.options || []).find((o) => text.includes(String(o.value).toLowerCase()));
      if (match) guesses[attr.attributeId] = match.optionId;
    });
    return { ...row, attributeSelections: guesses };
  });
}

// Validates a draft against its category's attributes and builds the payloads
// needed to create the product. Shared by the Edit form's Save and by
// "Add to store" (which auto-prepares products that only had a category set).
// Returns { draft } when ready, or { error } explaining what's missing.
function buildReadyDraft(product, input, categoryAttributes) {
  const trimmedName = (input.name || "").trim();
  const parsedPrice = toNumber(input.price);

  if (!input.categoryId) return { error: "Choose a category." };
  if (!trimmedName) return { error: "Enter a product name." };
  if (parsedPrice == null || parsedPrice < 0) return { error: "Enter a valid price." };

  const byOrder = (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
  const descriptiveAttributes = categoryAttributes.filter((a) => !a.isVariant).sort(byOrder);
  const variantAttributes = categoryAttributes.filter((a) => a.isVariant).sort(byOrder);
  const attributeValues = input.attributeValues || {};

  const attributeValuesPayload = descriptiveAttributes
    .map((attr) => {
      const value = attributeValues[attr.attributeId];
      if (!value) return null;
      if (attr.inputType === ATTRIBUTE_INPUT_DROPDOWN) {
        return value.optionId ? { attributeId: attr.attributeId, optionId: value.optionId } : null;
      }
      const trimmed = (value.textValue || "").trim();
      return trimmed ? { attributeId: attr.attributeId, textValue: trimmed } : null;
    })
    .filter(Boolean);
  const missingRequired = descriptiveAttributes.filter(
    (a) => a.isRequired && !attributeValuesPayload.some((v) => v.attributeId === a.attributeId)
  );
  if (missingRequired.length > 0) {
    return { error: `Missing required field${missingRequired.length > 1 ? "s" : ""}: ${missingRequired.map((a) => a.attributeName).join(", ")}` };
  }

  const variantRows = input.variantRows || [];
  const variantPayloads = [];
  if (hasRealVariants(product)) {
    if (variantAttributes.length === 0) {
      return { error: "This category has no variant attributes set up, so the detected variants can't be mapped. Pick a different category." };
    }
    for (let vi = 0; vi < product.variants.length; vi++) {
      const row = variantRows[vi] || { attributeSelections: {} };
      const mappedCount = variantAttributes.filter((a) => row.attributeSelections[a.attributeId]).length;
      if (mappedCount !== variantAttributes.length) {
        return { error: `Map every attribute for "${product.variants[vi].variant || product.variants[vi].options || `variant ${vi + 1}`}".` };
      }
      variantPayloads.push({
        price: toNumber(row.price) ?? parsedPrice,
        stockQuantity: toNumber(row.stockQuantity) ?? 0,
        sku: (row.sku || "").trim() || null,
        attributeValues: variantAttributes.map((a) => ({ attributeId: a.attributeId, optionId: Number(row.attributeSelections[a.attributeId]) })),
        imageUrl: product.variants[vi].image || "",
      });
    }
  }

  const imageSelection = [...(input.imageSelection || [])];
  const imageUrls = [...imageSelection].sort((a, b) => a - b).map((i) => product.images[i]).filter(Boolean);

  return {
    draft: {
      name: trimmedName,
      description: (input.description || "").trim(),
      price: parsedPrice,
      stockQuantity: toNumber(input.stockQuantity) ?? 0,
      sku: (input.sku || "").trim(),
      categoryId: input.categoryId,
      imageSelection,
      imageUrls,
      attributeValues,
      attributeValuesPayload,
      variantOverrides: Object.fromEntries(variantRows.map((row, vi) => [vi, row])),
      variantPayloads,
      ready: true,
    },
  };
}

function emptySourceState() {
  return {
    products: null,
    genericRows: null,
    genericColumns: [],
    fileName: "",
    error: "",
    search: "",
    expanded: new Set(),
    selected: new Set(),
    editingIndexes: new Set(),
    drafts: {},
    draftRevision: {},
    createdIds: {},
  };
}

export default function ScrapedProducts() {
  const [activeSource, setActiveSource] = useState("quickscrape");
  const [sourceState, setSourceState] = useState({
    quickscrape: emptySourceState(),
    productscout: emptySourceState(),
  });
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storeId, setStoreId] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [bulkCategoryId, setBulkCategoryId] = useState("");
  const [bulkPrice, setBulkPrice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitResults, setSubmitResults] = useState(null);

  const cur = sourceState[activeSource];
  const activeSourceConfig = SOURCES[activeSource];

  useEffect(() => {
    let active = true;
    setStoresLoading(true);
    getStoreOptions()
      .then((data) => {
        if (active) setStores(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setStores([]);
      })
      .finally(() => {
        if (active) setStoresLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!storeId) {
      setCategoryOptions([]);
      return;
    }
    let active = true;
    Promise.all([getCategories(), getStoreCategories(storeId)])
      .then(([allCategories, storeCategories]) => {
        if (!active) return;
        setCategoryOptions(buildCategoryOptions(allCategories, storeCategories));
      })
      .catch(() => {
        if (active) setCategoryOptions([]);
      });
    return () => {
      active = false;
    };
  }, [storeId]);

  function updateSource(key, patch) {
    setSourceState((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  function handleFile(source, file) {
    if (!file) return;
    updateSource(source, {
      error: "",
      fileName: file.name,
      products: null,
      genericRows: null,
      genericColumns: [],
      search: "",
      expanded: new Set(),
      selected: new Set(),
      drafts: {},
      createdIds: {},
    });
    setSubmitResults(null);

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        const items = Array.isArray(parsed) ? parsed : [parsed];
        if (items.length === 0) {
          updateSource(source, { products: [] });
          return;
        }

        if (isProductScoutShape(items)) {
          updateSource(source, { products: normalizeProductScout(items) });
        } else if (isGroupedShape(items)) {
          updateSource(source, { products: normalizeGrouped(items) });
        } else if (isFlatProductShape(items)) {
          updateSource(source, { products: normalizeFlat(items) });
        } else {
          const keys = new Set();
          items.forEach((item) => Object.keys(item || {}).forEach((k) => keys.add(k)));
          updateSource(source, { genericColumns: [...keys], genericRows: items });
        }
      } catch (e) {
        updateSource(source, {
          error: `Could not read that file as JSON. Make sure it's the file exported from ${SOURCES[source].label}.`,
        });
      }
    };
    reader.onerror = () => updateSource(source, { error: "Could not read that file." });
    reader.readAsText(file);
  }

  const filteredEntries = useMemo(() => {
    if (!cur.products) return null;
    const q = cur.search.trim().toLowerCase();
    return cur.products
      .map((p, i) => ({ p, i }))
      .filter(({ p }) => !q || p.product.toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q));
  }, [cur.products, cur.search]);

  const totalVariants = useMemo(
    () => (cur.products ? cur.products.reduce((sum, p) => sum + p.variants.length, 0) : 0),
    [cur.products]
  );

  function setSearch(value) {
    updateSource(activeSource, { search: value });
  }

  function toggleExpanded(index) {
    setSourceState((prev) => {
      const src = prev[activeSource];
      const next = new Set(src.expanded);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return { ...prev, [activeSource]: { ...src, expanded: next } };
    });
  }

  function toggleSelected(index) {
    setSourceState((prev) => {
      const src = prev[activeSource];
      const next = new Set(src.selected);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return { ...prev, [activeSource]: { ...src, selected: next } };
    });
  }

  // Selects every product currently shown (respects the search filter), or
  // clears them if they're all already selected.
  function toggleSelectAll() {
    const visible = (filteredEntries || []).map(({ i }) => i);
    setSourceState((prev) => {
      const src = prev[activeSource];
      const next = new Set(src.selected);
      const allSelected = visible.length > 0 && visible.every((i) => next.has(i));
      visible.forEach((i) => (allSelected ? next.delete(i) : next.add(i)));
      return { ...prev, [activeSource]: { ...src, selected: next } };
    });
  }

  function expandAll() {
    updateSource(activeSource, { expanded: new Set((filteredEntries || []).map(({ i }) => i)) });
  }

  function collapseAll() {
    updateSource(activeSource, { expanded: new Set() });
  }

  // Toggles the edit section for a single product. Expands its accordion too,
  // since the edit fields live inside the accordion body.
  function toggleProductEditing(index) {
    setSourceState((prev) => {
      const src = prev[activeSource];
      const nextEditing = new Set(src.editingIndexes);
      const nextExpanded = new Set(src.expanded);
      if (nextEditing.has(index)) {
        nextEditing.delete(index);
      } else {
        nextEditing.add(index);
        nextExpanded.add(index);
      }
      return { ...prev, [activeSource]: { ...src, editingIndexes: nextEditing, expanded: nextExpanded } };
    });
  }

  function saveDraft(index, draft) {
    const nextSelected = new Set(cur.selected);
    nextSelected.add(index);
    updateSource(activeSource, {
      drafts: { ...cur.drafts, [index]: draft },
      selected: nextSelected,
    });
  }

  // Bulk-set the category on every selected product's draft. This changes
  // which attributes/options are valid, so any variant mapping done for the
  // old category is cleared. "Add to store" re-prepares these automatically.
  function applyBulkCategory(categoryId) {
    if (!categoryId || cur.selected.size === 0) return;
    setSourceState((prev) => {
      const src = prev[activeSource];
      const nextDrafts = { ...src.drafts };
      const nextRevision = { ...src.draftRevision };
      src.selected.forEach((index) => {
        const existing = nextDrafts[index] || seedDraft(src.products[index]);
        const variantOverrides = Object.fromEntries(
          Object.entries(existing.variantOverrides || {}).map(([vi, row]) => [vi, { ...row, attributeSelections: {} }])
        );
        nextDrafts[index] = {
          ...existing,
          categoryId,
          attributeValues: {},
          variantOverrides,
          attributeValuesPayload: [],
          variantPayloads: [],
          ready: false,
        };
        nextRevision[index] = (nextRevision[index] || 0) + 1;
      });
      return { ...prev, [activeSource]: { ...src, drafts: nextDrafts, draftRevision: nextRevision } };
    });
  }

  // Bulk-set the price on every selected product's draft (base price, and
  // each variant's price if it already has one) without touching anything
  // else already prepared.
  function applyBulkPrice(value) {
    const parsed = toNumber(value);
    if (parsed == null || parsed < 0 || cur.selected.size === 0) return;
    setSourceState((prev) => {
      const src = prev[activeSource];
      const nextDrafts = { ...src.drafts };
      const nextRevision = { ...src.draftRevision };
      src.selected.forEach((index) => {
        const existing = nextDrafts[index] || seedDraft(src.products[index]);
        const variantOverrides = Object.fromEntries(
          Object.entries(existing.variantOverrides || {}).map(([vi, row]) => [vi, { ...row, price: parsed }])
        );
        const variantPayloads = (existing.variantPayloads || []).map((vp) => ({ ...vp, price: parsed }));
        nextDrafts[index] = { ...existing, price: parsed, variantOverrides, variantPayloads };
        nextRevision[index] = (nextRevision[index] || 0) + 1;
      });
      return { ...prev, [activeSource]: { ...src, drafts: nextDrafts, draftRevision: nextRevision } };
    });
  }

  async function handleAddToStore() {
    if (!storeId || cur.selected.size === 0) return;
    setSubmitting(true);
    setSubmitResults(null);
    const results = [];
    const preparedDrafts = {};
    const attributesCache = {};

    for (const index of cur.selected) {
      const product = cur.products[index];
      let draft = cur.drafts[index];

      // Not saved through Edit yet (e.g. only the bulk category was set):
      // prepare it here the same way Save does, using the bulk category if
      // the product has none of its own.
      if (!draft || !draft.ready) {
        const base = draft || seedDraft(product);
        const categoryId = base.categoryId || bulkCategoryId;
        const fail = (message) =>
          results.push({ index, name: base.name || product?.product || "(no name)", success: false, message });
        if (!categoryId) {
          fail('No category — pick one in "Set category for selected" or click Edit.');
          continue;
        }
        let categoryAttributes;
        try {
          if (!(categoryId in attributesCache)) {
            const data = await getCategoryAttributes(categoryId);
            attributesCache[categoryId] = Array.isArray(data) ? data : [];
          }
          categoryAttributes = attributesCache[categoryId];
        } catch {
          fail("Couldn't load the category's fields. Try again.");
          continue;
        }
        const variantRows = guessVariantSelections(
          product,
          categoryAttributes.filter((a) => a.isVariant),
          initialVariantRows(product, base)
        );
        const prepared = buildReadyDraft(product, { ...base, categoryId, variantRows }, categoryAttributes);
        if (prepared.error) {
          fail(`${prepared.error} — click Edit to finish it.`);
          continue;
        }
        draft = prepared.draft;
        preparedDrafts[index] = draft;
      }
      try {
        const created = await createProduct({
          storeId: Number(storeId),
          categoryId: draft.categoryId,
          name: draft.name,
          description: draft.description || null,
          price: draft.price,
          stockQuantity: draft.stockQuantity,
          sku: draft.sku || null,
          attributeValues: draft.attributeValuesPayload,
        });
        const productId = created.productId ?? created.id;

        let imageErrors = 0;
        for (const url of draft.imageUrls) {
          try {
            await addProductImageFromUrl(productId, url, product.link);
          } catch {
            // Non-fatal - keep going with the rest of the images, but surface the count below.
            imageErrors += 1;
          }
        }

        let variantErrors = 0;
        let variantImageErrors = 0;
        for (const vp of draft.variantPayloads) {
          try {
            const createdVariant = await createProductVariant(productId, {
              price: vp.price,
              stockQuantity: vp.stockQuantity,
              sku: vp.sku,
              attributeValues: vp.attributeValues,
            });
            if (vp.imageUrl) {
              try {
                await addVariantImageFromUrl(productId, createdVariant.variantId ?? createdVariant.id, vp.imageUrl, product.link);
              } catch {
                variantImageErrors += 1;
              }
            }
          } catch {
            variantErrors += 1;
          }
        }

        results.push({
          index,
          name: draft.name,
          success: true,
          productId,
          variantErrors,
          variantTotal: draft.variantPayloads.length,
          imageErrors,
          imageTotal: draft.imageUrls.length,
          variantImageErrors,
        });
      } catch (e) {
        const data = e?.response?.data;
        const message = (typeof data === "string" && data) || data?.title || data?.message || e.message || "Failed to create product.";
        results.push({ index, name: draft?.name || product?.product || "(no name)", success: false, message });
      }
    }

    const successIndexes = new Set(results.filter((r) => r.success).map((r) => r.index));
    const nextRevision = { ...cur.draftRevision };
    Object.keys(preparedDrafts).forEach((i) => {
      nextRevision[i] = (nextRevision[i] || 0) + 1;
    });
    updateSource(activeSource, {
      drafts: { ...cur.drafts, ...preparedDrafts },
      draftRevision: nextRevision,
      selected: new Set([...cur.selected].filter((i) => !successIndexes.has(i))),
      createdIds: {
        ...cur.createdIds,
        ...Object.fromEntries(results.filter((r) => r.success).map((r) => [r.index, r.productId])),
      },
    });
    setSubmitResults(results);
    setSubmitting(false);
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4">Scraped Products</Typography>
          <Typography variant="body2" color="text.secondary">
            {activeSourceConfig.hint}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            value={activeSource}
            exclusive
            size="small"
            onChange={(e, value) => value && setActiveSource(value)}
          >
            {Object.entries(SOURCES).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <ToggleButton key={key} value={key}>
                  <Icon fontSize="small" sx={{ mr: 1 }} />
                  {cfg.label}
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>

          <Button component="label" variant="contained" startIcon={<UploadFileIcon />}>
            Upload JSON
            <input
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(e) => handleFile(activeSource, e.target.files?.[0])}
            />
          </Button>
        </Stack>
      </Stack>

      {cur.error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => updateSource(activeSource, { error: "" })}>
          {cur.error}
        </Alert>
      )}

      {cur.products === null && cur.genericRows === null && !cur.error && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No file loaded yet. {activeSourceConfig.hint}
          </Typography>
        </Paper>
      )}

      {cur.products !== null && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <StorefrontOutlinedIcon fontSize="small" color="action" />
              <TextField
                select
                size="small"
                label="Destination store"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                sx={{ minWidth: 240 }}
                helperText={storesLoading ? "Loading stores…" : " "}
              >
                {stores.map((s) => (
                  <MenuItem key={s.storeId} value={s.storeId}>
                    {s.storeName}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Set category for selected"
                value={bulkCategoryId}
                onChange={(e) => {
                  setBulkCategoryId(e.target.value);
                  applyBulkCategory(e.target.value);
                }}
                sx={{ minWidth: 220 }}
                disabled={!storeId || cur.selected.size === 0}
                helperText=" "
              >
                {categoryOptions.map((c) => (
                  <MenuItem key={c.categoryId} value={c.categoryId}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>

              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  size="small"
                  type="number"
                  label="Set price for selected"
                  value={bulkPrice}
                  onChange={(e) => setBulkPrice(e.target.value)}
                  disabled={cur.selected.size === 0}
                  sx={{ width: 150 }}
                  helperText=" "
                />
                <Button
                  size="small"
                  variant="outlined"
                  disabled={cur.selected.size === 0 || toNumber(bulkPrice) == null}
                  onClick={() => applyBulkPrice(bulkPrice)}
                  sx={{ mt: "4px" }}
                >
                  Apply
                </Button>
              </Stack>

              <Chip size="small" color={cur.selected.size > 0 ? "primary" : "default"} label={`${cur.selected.size} selected`} />

              <Button
                variant="contained"
                disabled={!storeId || cur.selected.size === 0 || submitting}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                onClick={handleAddToStore}
              >
                Add {cur.selected.size || ""} to store
              </Button>

              {!storeId && (
                <Typography variant="caption" color="text.secondary">
                  Pick a store, then check products below (Edit lets you set the category and review fields first).
                </Typography>
              )}
            </Stack>
          </Paper>

          {submitResults && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Add to store results</Typography>
                <IconButton size="small" onClick={() => setSubmitResults(null)}>
                  ✕
                </IconButton>
              </Stack>
              <Stack spacing={1}>
                {submitResults.map((r, ri) => {
                  if (!r.success) {
                    return (
                      <Alert key={ri} severity="error" sx={{ py: 0 }}>
                        {`"${r.name}": ${r.message}`}
                      </Alert>
                    );
                  }
                  const failedImages = (r.imageErrors || 0) + (r.variantImageErrors || 0);
                  const parts = [`"${r.name}" added`];
                  if (r.variantTotal) parts.push(`with ${r.variantTotal - r.variantErrors}/${r.variantTotal} variants`);
                  if (r.imageTotal) parts.push(`${r.imageTotal - r.imageErrors}/${r.imageTotal} product images`);
                  return (
                    <Alert key={ri} severity={failedImages > 0 ? "warning" : "success"} sx={{ py: 0 }}>
                      {`${parts.join(", ")}.`}
                      {failedImages > 0 &&
                        ` ${failedImages} image${failedImages === 1 ? "" : "s"} failed to download from the source and were skipped.`}
                    </Alert>
                  );
                })}
              </Stack>
            </Paper>
          )}

          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              {(() => {
                const visibleCount = filteredEntries.length;
                const selectedVisible = filteredEntries.filter(({ i }) => cur.selected.has(i)).length;
                const all = visibleCount > 0 && selectedVisible === visibleCount;
                return (
                  <Stack direction="row" alignItems="center" sx={{ ml: -1 }}>
                    <Checkbox
                      size="small"
                      checked={all}
                      indeterminate={selectedVisible > 0 && !all}
                      disabled={visibleCount === 0}
                      onChange={toggleSelectAll}
                    />
                    <Typography variant="body2" sx={{ cursor: "pointer", userSelect: "none" }} onClick={toggleSelectAll}>
                      {all ? "Unselect all" : "Select all"}
                    </Typography>
                  </Stack>
                );
              })()}
              <Chip size="small" label={`${cur.products.length} product${cur.products.length === 1 ? "" : "s"}`} />
              <Chip size="small" label={`${totalVariants} variant${totalVariants === 1 ? "" : "s"}`} variant="outlined" />
              {cur.fileName && (
                <Typography variant="caption" color="text.secondary">
                  {cur.fileName}
                </Typography>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <TextField
                size="small"
                placeholder="Search by name or brand…"
                value={cur.search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 240 }}
              />
              <Tooltip title="Expand all">
                <IconButton size="small" onClick={expandAll}>
                  <UnfoldMoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Collapse all">
                <IconButton size="small" onClick={collapseAll}>
                  <UnfoldLessIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Paper>

          {filteredEntries.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No products match "{cur.search}".
            </Typography>
          )}

          <Stack spacing={1.5}>
            {filteredEntries.map(({ p, i }) => (
              <Accordion
                key={i}
                expanded={cur.expanded.has(i)}
                onChange={() => toggleExpanded(i)}
                disableGutters
                sx={{ "&:before": { display: "none" }, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%", pr: 1 }}>
                    <Checkbox
                      size="small"
                      checked={cur.selected.has(i)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelected(i)}
                    />

                    <Avatar variant="rounded" src={p.images[0]} sx={{ width: 48, height: 48, flexShrink: 0 }}>
                      <ImageOutlinedIcon />
                    </Avatar>

                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="subtitle1" noWrap title={p.product}>
                        {p.product}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        {p.brand && (
                          <Typography variant="caption" color="text.secondary">
                            {p.brand}
                          </Typography>
                        )}
                        {p.rating !== "" && (
                          <Stack direction="row" spacing={0.25} alignItems="center">
                            <StarIcon sx={{ fontSize: 14, color: "warning.main" }} />
                            <Typography variant="caption" color="text.secondary">
                              {p.rating}
                              {p.reviews ? ` (${p.reviews})` : ""}
                            </Typography>
                          </Stack>
                        )}
                        {p.category && <Chip size="small" variant="outlined" label={p.category} />}
                        {p.source && <Chip size="small" variant="outlined" label={p.source} />}
                        {cur.createdIds[i] != null && (
                          <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Added to store" />
                        )}
                      </Stack>
                    </Box>

                    {p.images.length > 1 && (
                      <AvatarGroup max={4} sx={{ "& .MuiAvatar-root": { width: 28, height: 28, fontSize: 12 } }}>
                        {p.images.map((src, imgI) => (
                          <Avatar key={imgI} src={src} variant="rounded" />
                        ))}
                      </AvatarGroup>
                    )}

                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={`${p.variants.length} variant${p.variants.length === 1 ? "" : "s"}`}
                    />

                    {p.link && (
                      <Tooltip title="Open source page">
                        <IconButton
                          size="small"
                          component="a"
                          href={p.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title={cur.editingIndexes.has(i) ? "Hide edit fields" : "Edit before adding to store"}>
                      <IconButton
                        size="small"
                        color={cur.editingIndexes.has(i) ? "primary" : "default"}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleProductEditing(i);
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 0 }}>
                  {p.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {p.description}
                    </Typography>
                  )}

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell>Variant</TableCell>
                          <TableCell>Options</TableCell>
                          <TableCell>SKU</TableCell>
                          <TableCell>Barcode</TableCell>
                          <TableCell>Price</TableCell>
                          <TableCell>Was</TableCell>
                          <TableCell>Discount</TableCell>
                          <TableCell>Qty</TableCell>
                          <TableCell>Weight</TableCell>
                          <TableCell>Availability</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {p.variants.map((v, vi) => (
                          <TableRow key={vi} hover>
                            <TableCell padding="checkbox">
                              <Avatar variant="rounded" src={v.image || p.images[0]} sx={{ width: 32, height: 32 }}>
                                <ImageOutlinedIcon fontSize="small" />
                              </Avatar>
                            </TableCell>
                            <TableCell>{v.variant || "—"}</TableCell>
                            <TableCell>{v.options || "—"}</TableCell>
                            <TableCell>{v.sku || "—"}</TableCell>
                            <TableCell>{v.barcode || "—"}</TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>{formatPrice(v.price, p.currency)}</TableCell>
                            <TableCell sx={{ textDecoration: v.was ? "line-through" : "none", color: "text.secondary" }}>
                              {v.was ? formatPrice(v.was, p.currency) : "—"}
                            </TableCell>
                            <TableCell>
                              {v.discount ? <Chip size="small" color="error" label={`-${v.discount}`} /> : "—"}
                            </TableCell>
                            <TableCell>{v.qty !== "" ? v.qty : "—"}</TableCell>
                            <TableCell>{v.weight || "—"}</TableCell>
                            <TableCell>
                              {v.availability ? (
                                <Chip size="small" color={availabilityColor(v.availability)} label={v.availability} />
                              ) : (
                                "—"
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {p.link && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                      Source:{" "}
                      <MuiLink href={p.link} target="_blank" rel="noopener noreferrer">
                        {p.link}
                      </MuiLink>
                    </Typography>
                  )}

                  {cur.editingIndexes.has(i) && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <ProductDraftEditor
                        key={`${i}-${cur.draftRevision[i] || 0}`}
                        product={p}
                        draft={cur.drafts[i] || seedDraft(p)}
                        storeId={storeId}
                        categoryOptions={categoryOptions}
                        onSave={(draft) => saveDraft(i, draft)}
                      />
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </>
      )}

      {cur.genericRows !== null && (
        <Paper>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
            <Chip size="small" label={`${cur.genericRows.length} row${cur.genericRows.length === 1 ? "" : "s"}`} />
            {cur.fileName && (
              <Typography variant="caption" color="text.secondary">
                {cur.fileName}
              </Typography>
            )}
          </Stack>
          <TableContainer sx={{ maxHeight: "70vh" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {cur.genericColumns.map((col) => (
                    <TableCell key={col}>{col}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {cur.genericRows.map((row, index) => (
                  <TableRow key={index} hover>
                    {cur.genericColumns.map((col) => (
                      <TableCell key={col} sx={{ maxWidth: 260 }}>
                        <Typography variant="body2" noWrap title={String(row[col] ?? "")}>
                          {String(row[col] ?? "")}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

function ProductDraftEditor({ product, draft, storeId, categoryOptions, onSave }) {
  const [name, setName] = useState(draft.name);
  const [description, setDescription] = useState(draft.description);
  const [price, setPrice] = useState(draft.price === "" ? "" : String(draft.price));
  const [stockQuantity, setStockQuantity] = useState(String(draft.stockQuantity ?? 0));
  const [sku, setSku] = useState(draft.sku);
  const [categoryId, setCategoryId] = useState(draft.categoryId);
  const [imageSelection, setImageSelection] = useState(new Set(draft.imageSelection));
  const [attributeValues, setAttributeValues] = useState(draft.attributeValues || {});
  const [categoryAttributes, setCategoryAttributes] = useState([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [formError, setFormError] = useState("");
  const [justSaved, setJustSaved] = useState(false);

  const productHasRealVariants = hasRealVariants(product);

  const [variantRows, setVariantRows] = useState(() => initialVariantRows(product, draft));

  const descriptiveAttributes = useMemo(
    () => categoryAttributes.filter((a) => !a.isVariant).sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [categoryAttributes]
  );
  const variantAttributes = useMemo(
    () => categoryAttributes.filter((a) => a.isVariant).sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [categoryAttributes]
  );

  useEffect(() => {
    if (!categoryId) {
      setCategoryAttributes([]);
      return;
    }
    let active = true;
    setLoadingAttributes(true);
    getCategoryAttributes(categoryId)
      .then((data) => {
        if (active) setCategoryAttributes(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setCategoryAttributes([]);
      })
      .finally(() => {
        if (active) setLoadingAttributes(false);
      });
    return () => {
      active = false;
    };
  }, [categoryId]);

  // Best-effort auto-match of each detected variant's raw text (e.g. "Black / M")
  // against the chosen category's variant option values, so the admin usually
  // only has to fix a few instead of mapping every one by hand.
  useEffect(() => {
    if (variantAttributes.length === 0) return;
    setVariantRows((prev) => guessVariantSelections(product, variantAttributes, prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantAttributes]);

  function toggleImage(imgIndex) {
    setImageSelection((prev) => {
      const next = new Set(prev);
      if (next.has(imgIndex)) next.delete(imgIndex);
      else next.add(imgIndex);
      return next;
    });
  }

  function setAttributeOptionValue(attributeId, optionId) {
    setAttributeValues((prev) => ({ ...prev, [attributeId]: { optionId, textValue: "" } }));
  }

  function setAttributeTextValue(attributeId, textValue) {
    setAttributeValues((prev) => ({ ...prev, [attributeId]: { optionId: null, textValue } }));
  }

  function updateVariantAttr(vi, attributeId, optionId) {
    setVariantRows((prev) =>
      prev.map((row, i) => (i === vi ? { ...row, attributeSelections: { ...row.attributeSelections, [attributeId]: optionId } } : row))
    );
  }

  function updateVariantField(vi, field, value) {
    setVariantRows((prev) => prev.map((row, i) => (i === vi ? { ...row, [field]: value } : row)));
  }

  function handleSave() {
    const result = buildReadyDraft(
      product,
      { name, description, price, stockQuantity, sku, categoryId, imageSelection: [...imageSelection], attributeValues, variantRows },
      categoryAttributes
    );
    if (result.error) {
      setFormError(result.error);
      return;
    }
    setFormError("");
    onSave(result.draft);
    setJustSaved(true);
  }

  function resetToSeed() {
    const seed = seedDraft(product);
    setName(seed.name);
    setDescription(seed.description);
    setPrice(seed.price === "" ? "" : String(seed.price));
    setStockQuantity(String(seed.stockQuantity ?? 0));
    setSku(seed.sku);
    setCategoryId(seed.categoryId);
    setImageSelection(new Set(seed.imageSelection));
    setAttributeValues({});
    setVariantRows(
      product.variants.map((v) => ({
        attributeSelections: {},
        price: toNumber(v.price) ?? "",
        stockQuantity: toNumber(v.qty) ?? 0,
        sku: v.sku || "",
      }))
    );
    setFormError("");
    setJustSaved(false);
  }

  return (
    <Box>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Edit before adding to store</Typography>
          {draft.ready && !justSaved && <Chip size="small" color="success" variant="outlined" label="Prepared" />}
          {justSaved && <Chip size="small" color="success" label="Saved" />}
        </Stack>
        {!storeId && <Alert severity="warning">Pick a destination store at the top of the page first.</Alert>}
        {formError && <Alert severity="error">{formError}</Alert>}

          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth autoFocus />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          <TextField
            select
            label="Category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            fullWidth
            disabled={!storeId}
            helperText={storeId && categoryOptions.length === 0 ? "This store has no categories enabled yet." : " "}
          >
            {categoryOptions.map((c) => (
              <MenuItem key={c.categoryId} value={c.categoryId}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={2}>
            <TextField label="Price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} fullWidth />
            <TextField
              label="Stock Quantity"
              type="number"
              value={stockQuantity}
              onChange={(e) => setStockQuantity(e.target.value)}
              fullWidth
            />
            <TextField label="SKU" value={sku} onChange={(e) => setSku(e.target.value)} fullWidth />
          </Stack>

          {product.images.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Images to include
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {product.images.map((src, imgI) => (
                  <Box key={imgI} sx={{ position: "relative" }}>
                    <Avatar
                      variant="rounded"
                      src={src}
                      sx={{ width: 64, height: 64, opacity: imageSelection.has(imgI) ? 1 : 0.35, cursor: "pointer" }}
                      onClick={() => toggleImage(imgI)}
                    />
                    <Checkbox
                      size="small"
                      checked={imageSelection.has(imgI)}
                      onChange={() => toggleImage(imgI)}
                      sx={{ position: "absolute", top: -8, right: -8, bgcolor: "background.paper", p: 0.25 }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {categoryId && descriptiveAttributes.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Category details
              </Typography>
              <Stack spacing={1.5}>
                {descriptiveAttributes.map((attr) => (
                  <Box key={attr.attributeId}>
                    {attr.inputType === ATTRIBUTE_INPUT_DROPDOWN ? (
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label={attr.attributeName + (attr.isRequired ? " *" : "")}
                        value={attributeValues[attr.attributeId]?.optionId ?? ""}
                        onChange={(e) => setAttributeOptionValue(attr.attributeId, Number(e.target.value))}
                      >
                        {(attr.options || []).map((o) => (
                          <MenuItem key={o.optionId} value={o.optionId}>
                            {o.value}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <TextField
                        size="small"
                        fullWidth
                        type={attr.inputType === ATTRIBUTE_INPUT_NUMBER ? "number" : "text"}
                        label={attr.attributeName + (attr.isRequired ? " *" : "")}
                        value={attributeValues[attr.attributeId]?.textValue ?? ""}
                        onChange={(e) => setAttributeTextValue(attr.attributeId, e.target.value)}
                      />
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {productHasRealVariants && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Map detected variants
              </Typography>
              {!categoryId && <Alert severity="info">Choose a category above to map these variants.</Alert>}
              {loadingAttributes && <Typography variant="caption">Loading attributes…</Typography>}
              {categoryId && !loadingAttributes && variantAttributes.length === 0 && (
                <Alert severity="warning">This category has no variant-generating attributes set up.</Alert>
              )}
              {categoryId && variantAttributes.length > 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Detected</TableCell>
                        {variantAttributes.map((a) => (
                          <TableCell key={a.attributeId}>{a.attributeName}</TableCell>
                        ))}
                        <TableCell>Price</TableCell>
                        <TableCell>Stock</TableCell>
                        <TableCell>SKU</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {product.variants.map((v, vi) => (
                        <TableRow key={vi}>
                          <TableCell sx={{ minWidth: 120 }}>
                            <Typography variant="body2">{v.variant || v.options || `Variant ${vi + 1}`}</Typography>
                            {v.variant && v.options && (
                              <Typography variant="caption" color="text.secondary">
                                {v.options}
                              </Typography>
                            )}
                          </TableCell>
                          {variantAttributes.map((a) => (
                            <TableCell key={a.attributeId} sx={{ minWidth: 130 }}>
                              <TextField
                                select
                                size="small"
                                fullWidth
                                value={variantRows[vi]?.attributeSelections?.[a.attributeId] ?? ""}
                                onChange={(e) => updateVariantAttr(vi, a.attributeId, Number(e.target.value))}
                              >
                                <MenuItem value="">—</MenuItem>
                                {(a.options || []).map((o) => (
                                  <MenuItem key={o.optionId} value={o.optionId}>
                                    {o.value}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </TableCell>
                          ))}
                          <TableCell sx={{ width: 100 }}>
                            <TextField
                              type="number"
                              size="small"
                              fullWidth
                              value={variantRows[vi]?.price ?? ""}
                              onChange={(e) => updateVariantField(vi, "price", e.target.value)}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 90 }}>
                            <TextField
                              type="number"
                              size="small"
                              fullWidth
                              value={variantRows[vi]?.stockQuantity ?? ""}
                              onChange={(e) => updateVariantField(vi, "stockQuantity", e.target.value)}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 110 }}>
                            <TextField
                              size="small"
                              fullWidth
                              value={variantRows[vi]?.sku ?? ""}
                              onChange={(e) => updateVariantField(vi, "sku", e.target.value)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={resetToSeed}>Reset</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
